import React, { useRef, useState } from 'react';

import { IconButton, InputAdornment, TextField } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useDebouncedCallback } from 'use-debounce';

import styles from './styles';

export const HelperText = ({ error, helperText }) =>
  error && helperText ? (
    <>
      <ErrorOutlineIcon />
      <span>{helperText}</span>
    </>
  ) : helperText ? (
    helperText
  ) : null;

export const CancelButton = ({
  hideClearIcon,
  clearIndicatorStyle,
  clearIndicatorDirtyStyle,
  textValue,
  onClear,
  handleChange,
  handleReset,
}) => {
  const className = clsx(clearIndicatorStyle, {
    [clearIndicatorDirtyStyle]: Boolean(textValue),
  });

  const handleClick = () => {
    handleReset();
    handleChange({ target: { value: '' } });
    typeof onClear === 'function' && onClear();
  };

  return !hideClearIcon ? (
    <IconButton onClick={handleClick} className={className}>
      <CancelOutlinedIcon />
    </IconButton>
  ) : null;
};

export const EndAdornment = ({
  endAdornment,
  hideClearIcon,
  clearIndicatorStyle,
  clearIndicatorDirtyStyle,
  textValue,
  onClear,
  handleChange,
  handleReset,
}) => {
  return (
    <InputAdornment position='end'>
      {endAdornment}
      <CancelButton
        hideClearIcon={hideClearIcon}
        clearIndicatorStyle={clearIndicatorStyle}
        clearIndicatorDirtyStyle={clearIndicatorDirtyStyle}
        textValue={textValue}
        onClear={onClear}
        handleChange={handleChange}
        handleReset={handleReset}
      />
    </InputAdornment>
  );
};

export const TextInput = props => {
  const {
    value: initialValue = '',
    classes: classesProp,
    className,
    onChange: onChangeProp,
    onClear,
    onKeyPress: onKeyPressProp,
    endAdornment,
    additionalInputProps,
    error,
    hideClearIcon = false,
    helperText,
    keyValuesToWatch = [],
    delay = 2,
    ...other
  } = props;
  const textContent = useRef('');
  const [value, setValue] = useState(initialValue);

  const {
    textInputRoot: textInputRootStyle,
    clearIndicator: clearIndicatorStyle,
    clearIndicatorDirty: clearIndicatorDirtyStyle,
    formHelperMessage: formHelperMessageStyle,
    ...classes
  } = classesProp;

  const handleOnChangeDebounce = useDebouncedCallback(value => {
    setValue(value);
    if (typeof onChangeProp === 'function') {
      onChangeProp({ target: { value } });
    }
  }, delay);

  const handleKeyPress = event => {
    if (!keyValuesToWatch.length) return;
    else if (keyValuesToWatch.includes(event.key) && onKeyPressProp) {
      onKeyPressProp(event);
    }
  };

  const handleChange = event => handleOnChangeDebounce(event.target.value);
  const handleReset = () => (textContent.current.value = '');

  return (
    <TextField
      data-testid='text-field'
      defaultValue={value}
      className={clsx(className, textInputRootStyle)}
      InputProps={{
        classes,
        endAdornment: (
          <EndAdornment
            endAdornment={endAdornment}
            hideClearIcon={hideClearIcon}
            clearIndicatorStyle={clearIndicatorStyle}
            clearIndicatorDirtyStyle={clearIndicatorDirtyStyle}
            onClear={onClear}
            textValue={value}
            handleChange={handleChange}
            handleReset={handleReset}
          />
        ),
        ...additionalInputProps,
      }}
      inputRef={textContent}
      variant='outlined'
      error={error}
      onChange={handleChange}
      onKeyPress={handleKeyPress}
      fullWidth
      FormHelperTextProps={{ classes: { contained: formHelperMessageStyle } }}
      helperText={helperText && <HelperText error={error} helperText={helperText} />}
      {...other}
    />
  );
};

TextInput.propTypes = {
  /** The amount of time to wait before invoking onchange values */
  delay: PropTypes.number,
  classes: PropTypes.object,
  className: PropTypes.string,
  /** Function called after text input changes. Takes in event prop. Value accessed at event.target.value*/
  onChange: PropTypes.func,
  /** Custom clear event that fires when clear button is pressed */
  onClear: PropTypes.func,
  /** Additional end adornment outside clearIcon. All endAdornment can be overwritten via InputProps */
  endAdornment: PropTypes.node,
  /** Additional InputProps outside of what is predefined. InputProps can also be overwritten */
  additionalInputProps: PropTypes.object,
  /** Indicates error state */
  error: PropTypes.bool,
  /** Text to show below TextInput */
  helperText: PropTypes.string,
  /** Show/hide clear icon */
  hideClearIcon: PropTypes.bool,
  keyValuesToWatch: PropTypes.array,
  /** Initial text value */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default withStyles(styles)(TextInput);
